// `export ... from` below re-exports these without binding them locally,
// and this file still references them itself.
import {
  type Layout,
  type TextCase,
  TextStyleSchema,
} from "@convex/model/composition";
import type { FontInfo as RemotionFontInfo } from "@remotion/google-fonts/base";
import { z } from "zod";
import type { Overlay } from "../components/rve/types";
import type { Database, Tables } from "./database.types";

// Define the Zod schema for Syllable
const SyllableSchema = z.object({
  id: z.number(),
  text: z.string(),
  endOffset: z.number().min(0).max(1), // fraction (0-1) of where this syllable ends within the word
});

export type Syllable = z.infer<typeof SyllableSchema>;
export interface SyllableWithFrames extends Syllable {
  startFrame: number;
  endFrame: number;
}

// Define the Zod schema for Word
const WordSchema = z.object({
  id: z.number(),
  text: z.string(),
  timestamp: z.number().optional(), // seconds as float
  duration: z.number().optional(), // seconds as float
  syllables: z.array(SyllableSchema).optional(), // Optional syllable breakdown
});

// Generate the Word type from the schema
export type Word = z.infer<typeof WordSchema>;

// Define the Zod schema for Line
export const LineSchema = z.object({
  id: z.number(),
  words: z.array(WordSchema),
  singer: z.array(z.number()).optional(),
});

// Generate the Line type from the schema
export type Line = z.infer<typeof LineSchema>;

export const LyricsSchema = z.array(LineSchema);

/**
 * A Chord holds for a span of the song's audio timeline. Detected from audio,
 * so Chords cover the whole song, including Breaks. `chord` is the detector's
 * label, stored and passed through verbatim.
 */
export const ChordSchema = z.object({
  start: z.number(),
  end: z.number(),
  /**
   * The detector returns the same Chord in several notations and at several
   * levels of detail. Pop notation is what a guitarist reads; jazz ("C-") and
   * Nashville ("1-") are stored verbatim but not displayed.
   */
  chord_complex_pop: z.string().nullish(),
  chord_simple_pop: z.string().nullish(),
  chord_basic_pop: z.string().nullish(),
  /**
   * Separate from the label: a slash chord names two pitches. Explicitly
   * `null` on every Chord in root position — which is most of them — so this
   * must be nullish, not merely optional, or the whole payload fails to parse.
   */
  bass: z.string().nullish(),
});
export type Chord = z.infer<typeof ChordSchema>;

export const ChordsSchema = z.array(ChordSchema);

/**
 * A Chord with the detector's other notations still attached. `ChordSchema`
 * strips them, which is right for display but destructive for editing: the
 * editor writes the whole array back, so anything it dropped on the way in
 * would be lost on the way out. Editing reads and writes this shape instead,
 * and only ever touches the fields it means to change.
 */
export const RawChordSchema = ChordSchema.loose();
export type RawChord = z.infer<typeof RawChordSchema>;

export const RawChordsSchema = z.array(RawChordSchema);

/**
 * Pitch of the project's audio slots, in cents. Chords are transposed for
 * display from this; the instrumental governs, else the original.
 */
export const PitchedAudioSchema = z.object({
  instrumentalCents: z.number().optional(),
  originalCents: z.number().optional(),
});
export type PitchedAudio = z.infer<typeof PitchedAudioSchema>;

// Import schemas for strict validation
const WordImportSchema = WordSchema.extend({
  duration: z.number().positive().optional(), // Replaces duration from WordSchema, enforcing positive for imports
});

const LineImportSchema = LineSchema.extend({
  words: z.array(WordImportSchema),
});

export const LyricsImportSchema = z.array(LineImportSchema);

export type {
  DefaultLayout,
  DualLanguageLayout,
  DuetLayout,
  KaraokeLayout,
  Layout,
  LinesPerScreen,
  ScrollLayout,
  SingleLayout,
  SingleLayoutAnimation,
  TextCase,
  TranslationData,
  VerticalPosition,
} from "@convex/model/composition";
// Stored shapes owned by the backend. Re-exported so existing importers of
// `@/types/types` keep working; new frontend code may import either path.
export {
  DefaultLayoutSchema,
  DualLanguageLayoutSchema,
  DuetLayoutSchema,
  KaraokeLayoutSchema,
  LayoutSchema,
  ScrollLayoutSchema,
  SingleLayoutSchema,
  TextStyleSchema,
  TranslationDataSchema,
  VerticalPositionSchema,
} from "@convex/model/composition";

export const ChordLanePositionSchema = z
  .enum(["top", "bottom"])
  .catch("bottom")
  .default("bottom");
export type ChordLanePosition = z.infer<typeof ChordLanePositionSchema>;

export const ChordLaneSchema = z.object({
  /**
   * Display choice only. Turning the lane off never deletes detected Chords,
   * and keeping the other settings means turning it back on restores them.
   */
  enabled: z.boolean().catch(false).default(false),
  position: ChordLanePositionSchema,
  /** How many seconds of song the lane shows at once. Fixed scale. */
  windowSeconds: z.number().positive().catch(8).default(8),
  /** Display-only: nothing simplified is ever persisted. */
  simplify: z.boolean().catch(false).default(false),
  /**
   * The Chords' own look. Absent, the lane follows Voice 1, so a creator who
   * never touches it gets Chords that match the lyrics. Only the font, weight,
   * colors and outline apply: the size comes from the lane, and case is never
   * applied because chord notation is case-significant.
   */
  textStyle: TextStyleSchema.optional().catch(undefined),
});
export type ChordLaneSettings = z.infer<typeof ChordLaneSchema>;

export const TEXT_CASE_OPTIONS: Array<{
  value: TextCase;
  label: string;
}> = [
  { value: "none", label: "Normal" },
  { value: "uppercase", label: "Uppercase" },
  { value: "lowercase", label: "Lowercase" },
] as const;

export const ProjectMetadataSchema = z.object({
  hasFirstBasicVersion: z.boolean().default(false),
});

// export type TextCase = (typeof TEXT_CASE_OPTIONS)[number];

// Generate the TextStyle type from the schema

export type TextStyle = z.infer<typeof TextStyleSchema>;
export type WordIndex = [number, number];

// Audioshake
const AudioshakeWordAlignedSchema = z.object({
  start: z.number(),
  end: z.number(),
  text: z.string(),
});

const AudioshakeLineAlignedSchema = z.object({
  start: z.number(),
  end: z.number(),
  text: z.string(),
  words: z.array(AudioshakeWordAlignedSchema),
});

export const AudioshakeLyricsAlignmentResultSchema = z.object({
  text: z.string(),
  lines: z.array(AudioshakeLineAlignedSchema),
});

export type AudioshakeLyricsAlignmentResult = z.infer<
  typeof AudioshakeLyricsAlignmentResultSchema
>;

export type Profile = Tables<"profiles">;

/**
 * Subscription type from database.
 * The metadata column is JSONB and can contain:
 * - Stripe subscription metadata (from Stripe API)
 * - Our app-specific data like credits_included
 * Use parseSubscriptionMetadata() from @/types/subscription to parse typed metadata.
 */
export type Subscription = Tables<"subscriptions">;

/**
 * Plan tier including both subscription tiers and non-subscription states.
 * - "standard" | "premium": Active subscription tiers
 * - "payg": Pay-as-you-go customer (has purchased credits but no subscription)
 * - "free": Free user (never purchased credits or subscribed)
 */
export type PlanTier = Subscription["tier"] | "payg" | "free";

/**
 * Minimal credits interface used by the client-side context.
 * Credits are calculated from the credit_batches ledger (source of truth).
 */
export interface Credits {
  /** Available credits from non-expired batches */
  remaining_credits: number;
}

export type Project = Omit<
  Tables<"projects">,
  | "lyrics"
  | "singer_1_style"
  | "singer_2_style"
  | "singer_both_style"
  | "layout"
  | "metadata"
> & {
  layout: Layout;
  lyrics: Line[];
  singer_1_style: TextStyle | null;
  singer_2_style: TextStyle | null;
  singer_both_style: TextStyle | null;
  jobs?: Job[];
  render_jobs?: RenderJob[];
  metadata: z.infer<typeof ProjectMetadataSchema>;
};

export type ProjectVersion = Tables<"project_versions">;
export type ProjectVersionWithOverlays = Omit<ProjectVersion, "overlays"> & {
  overlays: Overlay[];
};

export type ProjectWithVersions = Project & {
  project_versions: ProjectVersionWithOverlays[];
};

export type RenderJob = Tables<"render_jobs">;

export type UploadedFile = {
  name: string;
  type: string | null;
  url: string;
  remotePath?: string;
  file?: File;
};

export type BackgroundJobType = Database["public"]["Enums"]["job_type_enum"];
export type Job = Tables<"jobs">;

export type ProjectAudioSlotKeyType =
  Database["public"]["Enums"]["project_audio_slot_key_type"];

export type ProjectPitchedAudio = Tables<"project_pitched_audios">;

export const RenderEventDataSchema = z.object({
  renderId: z.string(),
  status: z.enum(["pending", "rendering", "completed", "failed"]),
  url: z.string().url().nullable(),
  error: z.string().nullish(),
  duration: z.number().int().positive().nullish(),
  billableWorkerTime: z.number().int().positive().nullish(),
  cost: z.number().nonnegative().nullish(),
});

export const AlignedLyricsSchema = z.array(
  z.object({
    line: z.string(),
    words: z.array(
      z.object({
        start: z.number(),
        end: z.number(),
        word: z.string(),
        score: z.number(),
      }),
    ),
  }),
);
export type AlignedLyrics = z.infer<typeof AlignedLyricsSchema>;

export const SeparateVocalsOutputSchema = z.object({
  success: z.boolean(),
  data: z
    .object({
      original_url: z.string(),
      instrumental_url: z.string(),
      vocals_url: z.string(),
    })
    .optional(),
});

export const SeparateBackingVocalsOutputSchema = z.object({
  success: z.boolean(),
  data: z.object({
    lead_vocals_url: z.string(),
    backing_vocals_url: z.string(),
  }),
});

export type RenderJobGet = Pick<
  Tables<"render_jobs">,
  | "id"
  | "status"
  | "download_url"
  | "project_id"
  | "bucket_name"
  | "created_at"
  | "updated_at"
  | "metadata"
  | "render_credits_used"
  | "settings"
  | "project_version_id"
  | "progress"
> & {
  project_versions?: {
    duration_in_seconds: number;
  } | null;
};

export type FontInfo = RemotionFontInfo & {
  previewUrl?: string;
};

export type S3ClientName = "tigris" | "r2";
